import { useCallback } from 'react';

import type { PromotionExposureCandidate } from 'features/promotions/types';

import { RunningExperiments, useExperiments } from 'state/experiments';

interface BestPromotionExposureResult {
  userBirthdayDuration?: unknown;
}

interface PersonalizedPromotionsExperiment {
  collectionId?: string;
  isAssignmentPending: boolean;
  getExposureCandidateForResult: (
    promotion?: BestPromotionExposureResult | null
  ) => PromotionExposureCandidate | undefined;
}

export const parsePromotionCollectionId = (payload: unknown): string | undefined => {
  if (typeof payload !== 'string') {
    return;
  }

  const collectionId = payload.trim();
  return collectionId.length > 0 ? collectionId : undefined;
};

export const usePersonalizedPromotionsExperiment = (
  enabled: boolean
): PersonalizedPromotionsExperiment => {
  const { getVariantValue, isAssignmentLoading, logExposure } = useExperiments();
  const variant = getVariantValue(RunningExperiments.PersonalizedPromotions);
  const payload = variant?.payload;
  const collectionId = parsePromotionCollectionId(payload);
  const hasNoPayload = payload === undefined || payload === null;
  const hasConfiguredAssignment =
    variant?.key !== 'off' &&
    Boolean(variant?.value) &&
    (hasNoPayload || collectionId !== undefined);
  const isAssignmentPending = enabled && isAssignmentLoading;
  const isActive = enabled && !isAssignmentPending && hasConfiguredAssignment;

  const getExposureCandidateForResult = useCallback(
    (promotion?: BestPromotionExposureResult | null) => {
      if (!isActive || promotion === undefined) {
        return;
      }

      if (promotion === null) {
        return {
          kind: 'empty' as const,
          logExposure: () => logExposure(RunningExperiments.PersonalizedPromotions),
        };
      }

      // Birthday promotions always win the CMS ranking but are not part of this
      // experiment. An omitted duration on a returned promotion is also treated
      // conservatively as ineligible.
      if (promotion.userBirthdayDuration !== null) {
        return;
      }

      return {
        kind: 'promotion' as const,
        logExposure: () => logExposure(RunningExperiments.PersonalizedPromotions),
      };
    },
    [isActive, logExposure]
  );

  return {
    collectionId: isActive ? collectionId : undefined,
    isAssignmentPending,
    getExposureCandidateForResult,
  };
};
